import { DecimalUtils, DISPLAY_SCALE } from "../../../../libs/utils";
import { ReceiptInvoiceAllocationRow } from "./types";

const MONEY_SCALE = DISPLAY_SCALE;

const toMoneyNumber = (value: unknown) => DecimalUtils.toNumber(value as string | number | null | undefined, { scale: MONEY_SCALE });

const toCents = (value: unknown) => Math.round(toMoneyNumber(value) * 100);

const isAllocationRow = (row: any): row is ReceiptInvoiceAllocationRow =>
  typeof row?.allocation === "number" && typeof row?.selected === "boolean" && typeof row?.balance === "number";

export const mapToAllocationRow = (row: any, index: number): ReceiptInvoiceAllocationRow => ({
  id: row?.pdcId || row?._id,
  pdcId: row?.pdcId,
  invoiceNo: row?.invoiceNo || row?.invoiceNumber || row?.erpInvoiceNumber || "--",
  type: row?.type || row?.description || row?.descriptionLabel || row?.paymentType || "--",
  dueDate: row?.dueDate || "",
  invoiceAmount: Number(row?.totalAmount ?? 0),
  balance: Number(row?.balance ?? row?.totalAmount ?? 0),
  allocation: Number(row?.allocation ?? row?.balance ?? row?.totalAmount ?? 0),
  selected: row?.selected ?? true,
  removable: row?.removable ?? true,
});

export const normalizeInvoices = (rows: ReceiptInvoiceAllocationRow[] | any[]): ReceiptInvoiceAllocationRow[] => {
  if (!rows.length) return [];
  return rows.map((row, index) => (isAllocationRow(row) ? row : mapToAllocationRow(row, index)));
};

export const buildInitialAllocationRows = (
  rows: ReceiptInvoiceAllocationRow[] | any[],
  receiptTotal: number,
): ReceiptInvoiceAllocationRow[] => {
  const normalized = normalizeInvoices(rows);
  return distributeReceiptTotal(receiptTotal, normalized);
};

export const distributeReceiptTotal = (
  receiptTotal: number,
  invoices: ReceiptInvoiceAllocationRow[],
): ReceiptInvoiceAllocationRow[] => {
  let remainingCents = Math.max(0, toCents(receiptTotal));

  return invoices.map((invoice) => {
    if (!invoice.selected) {
      return { ...invoice, allocation: 0 };
    }

    const payableCents = Math.max(0, toCents(invoice.balance));
    const allocationCents = Math.min(payableCents, remainingCents);
    remainingCents -= allocationCents;

    return { ...invoice, allocation: toMoneyNumber(allocationCents / 100) };
  });
};

export const buildReceiptAllocations = (invoices: ReceiptInvoiceAllocationRow[] | any[]) =>
  normalizeInvoices(invoices)
    .filter((row) => row.selected && Number(row.allocation) > 0)
    .map((row) => ({
      pdcId: String(row.pdcId  || ""),
      allocatedAmount: Number(row.allocation),
    }))
    .filter((item) => item.pdcId);

export const getRemainingBalance = (invoice: ReceiptInvoiceAllocationRow) => {
  const balanceCents = Math.max(0, toCents(invoice.balance));
  const allocationCents = invoice.selected ? Math.max(0, toCents(invoice.allocation)) : 0;
  const remainingCents = Math.max(0, balanceCents - allocationCents);
  return toMoneyNumber(remainingCents / 100);
};

export const parseCurrencyInput = (value: string) => Number(value.replace(/[^\d.]/g, "")) || 0;

export const convertToNormalCase = (input: string = "") => {
  if (["directDebit", "directdebit"].includes(input)) {
    return "Direct Debit";
  }

  if (input.indexOf("_") === -1 && input !== input.toLowerCase()) {
    return input.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/\b\w/g, (char) => char.toUpperCase());
  }

  if (input.indexOf("_") !== -1) {
    return input.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  }

  return input.charAt(0).toUpperCase() + input.slice(1);
};
